import { AppLocation } from 'src/navigation/appLocations'
import * as Routes from 'src/navigation/urls'

/**
 * Profile flat route tree (EX-39748) — mirrors the canonical Budgets/Accounts
 * pattern (see budgetsRouteTree.ts / accountsRouteTree.ts), adapted to the
 * Profile ("Profile Settings") family's real shape.
 *
 * This is the ONE place that describes the Profile tab tree. Everything else
 * derives from it:
 *   - the flat-route GATE paths (`profileFamilyGatePaths`),
 *   - the per-route METADATA fed to `locationHooks` (sidenav active state,
 *     breadcrumbs, content type) — built in `flatArchitectureRouteMetadata.ts`.
 *
 * TWO DELIBERATE DIFFERENCES FROM BUDGETS (documented; see PR body):
 *   1. The Profile family is NOT a tabbed surface in the Budgets sense: there is
 *      NO in-page `Subnav` tab bar on `/profile`. Navigation between the profile
 *      sub-pages is driven by the persistent SETTINGS SIDENAV
 *      (`src/navigation/sidenav/useSettingsNavItems` + `SidenavContentType.Settings`),
 *      whose "Profile Settings" group highlights on the profile `AppLocation`s.
 *      So `ProfileFamilyPage` PRESERVES the legacy no-tab-bar rendering (a
 *      "Profile Settings" title + a nested `<Switch>`) exactly as
 *      `AccountsFamilyPage` does, rather than introducing a `FamilyLayout` tab
 *      bar that would change UX. This `tabs` list exists only to feed the gate +
 *      metadata; `labelSource` is unused for rendering (no tab bar).
 *   2. There is NO `/:id` entity detail view in this family — every route is a
 *      static `/profile/*` path. So this tree has only a `family` section (no
 *      `entity` section / param-child gate matcher).
 *
 * CONTENT-TYPE PARITY (important): the legacy `/profile` family root mapped to
 * `SidenavContentType.Main` (its `ProfileAppRoute.meta`), while every sub-page
 * mapped to `SidenavContentType.Settings`. That split is preserved in
 * `flatArchitectureRouteMetadata.ts` — do NOT collapse the sub-pages to `Main`.
 */

export interface ProfileFamilyTabNode {
  /** Stable key used for active-route detection. */
  key: string
  /** Translation key. (Unused for rendering — the family has no tab bar.) */
  labelSource: string
  /** AppLocation that drives sidenav active state via locationHooks. */
  appLocation: AppLocation
  /** Exact URL pattern, e.g. `/profile/overview`. */
  path: string
}

export const profileRouteTree = {
  family: {
    /** `/profile` — redirects to the first sub-page (Overview). */
    rootPath: Routes.profileUrl(),
    rootAppLocation: AppLocation.Profile,
    redirectTo: Routes.profileOverviewUrl(),
    tabs: [
      {
        key: 'overview',
        labelSource: 'Overview',
        appLocation: AppLocation.ProfileOverview,
        path: Routes.profileOverviewUrl(),
      },
      {
        key: 'info',
        labelSource: 'Personal Info',
        appLocation: AppLocation.ProfileInfo,
        path: Routes.profileInfoUrl(),
      },
      {
        key: 'notifications',
        labelSource: 'Notifications',
        appLocation: AppLocation.ProfileNotifications,
        path: Routes.profileNotificationsUrl(),
      },
      {
        key: 'security',
        labelSource: 'Security',
        appLocation: AppLocation.ProfileSecurity,
        path: Routes.profileSecurityUrl(),
      },
      {
        // `/profile/manage` (legacy `profileDeleteUrl`) — the "Manage Account"
        // sub-page. Static path, full page under the profile shell.
        key: 'manage',
        labelSource: 'Manage',
        appLocation: AppLocation.ProfileDelete,
        path: Routes.profileDeleteUrl(),
      },
      {
        // `/profile/overview/apikeys` (legacy `apiKeysProfileUrl`) — a 3-segment
        // static path nested under overview. With exact matchPath it never
        // collides with `/profile/overview`; both resolve to ProfileFamilyPage.
        key: 'apikeys',
        labelSource: 'API Keys',
        appLocation: AppLocation.ProfileApiKeys,
        path: Routes.apiKeysProfileUrl(),
      },
    ] as ProfileFamilyTabNode[],
  },
}

/**
 * GATE paths for the family page: the family root plus every static sub-page
 * path. Enumerated (not parameterized) — every path is static, so there is no
 * `:id` route to disambiguate against and no param-child matcher is needed.
 *
 * Accepted, documented delta (checklist item: unknown-child fallback): the
 * legacy `ProfileAppRoute` used `{ path: '/profile', exact: false }`, so an
 * unknown child (e.g. `/profile/zzz-unknown`, a non-linked typo URL) rendered the
 * profile-scoped in-page not-found inside the "Profile Settings" chrome. With the
 * exact static gate, such a URL misses the flat gate and renders the GLOBAL
 * not-found inside the authenticated shell (Sidenav + TopBar) instead. Both are
 * authenticated not-found pages; no URL becomes unreachable. This matches the
 * static-only Budgets/Accounts FAMILY gates (which also enumerate exact paths) —
 * adding a `/profile/:rest+` catch-all would be a redesign of the proven pattern
 * and risk capturing the legacy overlay routes, so it is intentionally not done.
 */
export const profileFamilyGatePaths: string[] = [
  profileRouteTree.family.rootPath,
  ...profileRouteTree.family.tabs.map((t) => t.path),
]
